import { useEffect, useState } from 'react'
import { api } from '../../lib/api.js'
import { Card, ErrorBox, Field, Page, Spinner, SubmitButton, useAsync, useConfirm, useToast } from '../../components/Ui.jsx'

const FIELDS = [
  ['commission_pct', 'Platform commission (%)', 'Deducted from the hatchery payout on every order.'],
  ['advance_pct', 'Suggested advance (%)', 'Used to propose the block deposit. The operator can override it per order.'],
  ['date_window_days', 'Stocking date window (+days)', 'Farmers can postpone but never stock early, so the window is one-sided: target date to target +N.'],
  ['sample_lead_days', 'Sample balloon lead (days)', 'How many days before stocking the 100-animal sample must reach the farmer.'],
  ['sample_animals', 'Animals per sample balloon', 'Standard count sent for the 24-hour pond survival test.'],
  ['pass_threshold_pct', 'Sample pass threshold (%)', 'At or above this survival the lot is accepted; below it the order is re-matched.'],
  ['public_show_prices', 'Show prices on the public directory (0 or 1)', '0 keeps prices and offers off the public hatchery pages, so the desk quotes them on the call. 1 publishes them.']
]

export default function Settings () {
  const { data, error, loading, reload } = useAsync(() => api.get('/admin/settings'))
  const toast = useToast()
  const confirm = useConfirm()
  const [form, setForm] = useState({})
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (data?.settings) setForm(data.settings) }, [data])

  const submit = async e => {
    e.preventDefault()
    const showingPrices = Number(form.public_show_prices) === 1
    const wasShowing = Number(data.settings.public_show_prices) === 1
    const ok = await confirm({
      title: 'Save these platform rules?',
      body: 'Every new booking uses these values from the moment you save. Orders already on the desk keep the numbers they were created with.',
      facts: [
        ['Commission', `${form.commission_pct}%`],
        ['Sample threshold', `${form.pass_threshold_pct}%`],
        ['Date window', `+${form.date_window_days} days`],
        showingPrices !== wasShowing && ['Public prices', showingPrices ? 'ON — farmers will see rates' : 'OFF — the desk quotes on the call']
      ],
      confirmLabel: 'Save settings',
      warning: showingPrices !== wasShowing
        ? 'This changes what every farmer with the link can see.'
        : null
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.put('/admin/settings', form)
      setForm(res.settings)
      toast.ok('Settings saved. New bookings use these values immediately.')
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  if (loading) return <Page title="Settings"><Spinner /></Page>
  if (error) return <Page title="Settings"><ErrorBox error={error} onRetry={reload} /></Page>

  return (
    <Page
      title="Settings"
      subtitle="The commercial and quality rules the whole desk runs on. Changes apply to new bookings; orders already in flight keep the terms they were booked under."
    >
      <div className="wrap narrow" style={{ padding: 0, maxWidth: 720 }}>
        <Card title="Platform rules">
          <form onSubmit={submit}>
            {FIELDS.map(([key, label, hint]) => (
              <Field key={key} label={label} hint={hint}>
                <input
                  type="number"
                  step="0.1"
                  value={form[key] ?? ''}
                  onChange={e => setForm({ ...form, [key]: e.target.value })}
                />
              </Field>
            ))}
            <SubmitButton busy={busy} type="submit">Save settings</SubmitButton>
          </form>
        </Card>
      </div>
    </Page>
  )
}
